<script setup lang="ts">
import { dataProcessingPolicy as policyDocument } from '~/shared-kernel/domain/data-processing-policy'

// Modal mode: scrollable, view-only reader (h3/h4). Standalone mode: public page flow (h2/h3), copyable and printable.
const props = defineProps<{ standalone?: boolean }>()
const { t } = useI18n()
const viewport = ref<HTMLElement | null>(null)
const indexOpen = ref(false)
const sectionTag = computed(() => (props.standalone ? 'h2' : 'h3'))
const subTag = computed(() => (props.standalone ? 'h3' : 'h4'))
const sections = computed(() => [
  { id: 'overview', number: null, title: t('dataProcessingPolicy.overview'), blocks: policyDocument.introduction },
  ...policyDocument.sections
])

async function goToSection(id: string) {
  indexOpen.value = false
  await nextTick()
  const target = viewport.value?.querySelector<HTMLElement>(`#policy-${id}`)
  if (!target || !viewport.value) return
  viewport.value.scrollTop += target.getBoundingClientRect().top - viewport.value.getBoundingClientRect().top
  target.focus({ preventScroll: true })
}

async function reset() {
  indexOpen.value = false
  await nextTick()
  if (viewport.value) viewport.value.scrollTop = 0
}

defineExpose({ reset })
</script>

<template>
  <article class="data-processing-policy-document" :class="{ 'data-processing-policy-document--standalone': standalone }"
    v-on="standalone ? {} : { copy: (e: Event) => e.preventDefault(), cut: (e: Event) => e.preventDefault(), paste: (e: Event) => e.preventDefault(), contextmenu: (e: Event) => e.preventDefault(), dragstart: (e: Event) => e.preventDefault() }">
    <div v-if="!standalone" class="policy-banner">
      <span class="policy-banner__icon"><UIcon name="i-lucide-shield-check" aria-hidden="true" /></span>
      <div>
        <p class="policy-banner__brand">TIQUETE360 S.A.S.</p>
        <p>{{ t('dataProcessingPolicy.viewOnly') }}</p>
      </div>
    </div>
    <div ref="viewport" class="policy-reader" :tabindex="standalone ? undefined : 0" :aria-label="standalone ? undefined : t('dataProcessingPolicy.content')">
      <section class="policy-quality" aria-labelledby="policy-quality-title">
        <div class="policy-quality__heading">
          <UIcon name="i-lucide-badge-check" aria-hidden="true" />
          <div>
            <component :is="sectionTag" id="policy-quality-title" class="policy-quality__system">{{ policyDocument.qualityManagement.system }}</component>
            <p>{{ policyDocument.qualityManagement.process }}</p>
          </div>
        </div>
        <p class="policy-quality__title">{{ policyDocument.qualityManagement.title }}</p>
        <dl>
          <div><dt>{{ t('dataProcessingPolicy.qualityCode') }}</dt><dd>{{ policyDocument.qualityManagement.code }}</dd></div>
          <div><dt>{{ t('dataProcessingPolicy.qualityVersion') }}</dt><dd>{{ policyDocument.qualityManagement.revision }}</dd></div>
          <div><dt>{{ t('dataProcessingPolicy.qualityDate') }}</dt><dd>{{ policyDocument.qualityManagement.issuedOn }}</dd></div>
        </dl>
      </section>
      <nav v-if="standalone" class="policy-index policy-index--static" :aria-label="t('dataProcessingPolicy.index')">
        <ol class="policy-index__items">
          <li v-for="section in policyDocument.sections" :key="section.id">
            <a :href="`#policy-${section.id}`">
              <span v-if="section.number" class="policy-index__number" aria-hidden="true">{{ section.number }}</span>
              <span>{{ section.title }}</span>
            </a>
          </li>
        </ol>
      </nav>
      <UCollapsible v-else v-model:open="indexOpen" class="policy-index">
        <TqButton variant="secondary" icon="i-lucide-list" class="policy-index__toggle">
          {{ t('dataProcessingPolicy.index') }}
          <UIcon :name="indexOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" aria-hidden="true" />
        </TqButton>
        <template #content>
          <nav class="policy-index__items" :aria-label="t('dataProcessingPolicy.index')">
            <TqButton v-for="section in policyDocument.sections" :key="section.id" variant="ghost" compact @click="goToSection(section.id)">
              <span v-if="section.number" class="policy-index__number" aria-hidden="true">{{ section.number }}</span>
              <span>{{ section.title }}</span>
            </TqButton>
          </nav>
        </template>
      </UCollapsible>
      <section v-for="section in sections" :key="section.id" class="policy-section" :aria-labelledby="`policy-${section.id}`">
        <component :is="sectionTag" :id="`policy-${section.id}`" class="policy-section__title" tabindex="-1">
          <span v-if="section.number" class="policy-section__number" aria-hidden="true">{{ section.number }}</span>
          {{ section.title }}
        </component>
        <template v-for="(block, index) in section.blocks" :key="index">
          <p v-if="block.kind === 'paragraph'">{{ block.text }}</p>
          <component :is="subTag" v-else-if="block.kind === 'heading'" class="policy-section__subtitle">{{ block.text }}</component>
          <ul v-else-if="block.kind === 'list'">
            <li v-for="(item, itemIndex) in block.items" :key="itemIndex">{{ item }}</li>
          </ul>
          <div v-else-if="block.kind === 'table'" class="policy-table" :class="{ 'policy-table--pairs': block.headers.length === 2 }" tabindex="0" :aria-label="section.title">
            <table>
              <thead><tr><th v-for="(header, headerIndex) in block.headers" :key="headerIndex" scope="col">{{ header }}</th></tr></thead>
              <tbody>
                <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                  <th scope="row">{{ row[0] }}</th>
                  <td v-for="(cell, cellIndex) in row.slice(1)" :key="cellIndex">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </section>
    </div>
  </article>
</template>

<style scoped>
.data-processing-policy-document {
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  color: var(--tq-text);
}
.data-processing-policy-document--standalone { user-select: text; -webkit-user-select: text; -webkit-touch-callout: default; }
.policy-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-muted);
}
.policy-banner__icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--tq-radius-md);
  background: var(--tq-bg-elevated);
  color: var(--tq-teal-readable);
  font-size: 1.4rem;
}
.policy-banner p { margin: 0; color: var(--tq-text-muted); font-size: .875rem; }
.policy-banner .policy-banner__brand { margin-bottom: .25rem; color: var(--tq-text); font-weight: 700; letter-spacing: .06em; }
.policy-reader {
  position: relative;
  max-height: 60dvh;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding: .25rem .75rem .5rem .25rem;
}
.data-processing-policy-document--standalone .policy-reader { max-height: none; overflow: visible; padding: 0; }
.policy-index {
  padding: .75rem;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-elevated);
}
.policy-quality { margin-bottom: 1rem; padding: 1.25rem; border: 1px solid var(--tq-border); border-radius: var(--tq-radius-lg); background: var(--tq-bg-elevated); }
.policy-quality__heading { display: flex; align-items: flex-start; gap: .75rem; }
.policy-quality__heading > :deep(.iconify) { flex: 0 0 auto; margin-top: .2rem; color: var(--tq-teal-readable); font-size: 1.25rem; }
.policy-quality__system { margin: 0; font-size: 1rem; line-height: 1.5; font-weight: 700; }
.policy-quality__heading p { margin: .35rem 0 0; color: var(--tq-text-muted); font-size: .8rem; letter-spacing: .04em; }
.policy-quality__title { margin: 1rem 0; font-weight: 600; font-size: .9rem; line-height: 1.6; }
.policy-quality dl { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .75rem; margin: 0; }
.policy-quality dl > div { padding: .75rem; border-radius: var(--tq-radius-md); background: var(--tq-bg-muted); }
.policy-quality dt { font-size: .8rem; color: var(--tq-text-muted); }
.policy-quality dd { margin: .35rem 0 0; font-size: .95rem; font-weight: 600; overflow-wrap: anywhere; }
.policy-index :deep([data-slot='content']) { animation: none; }
.policy-index__toggle { width: 100%; justify-content: space-between; }
.policy-index__items { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .25rem .75rem; margin-top: .75rem; }
.policy-index--static .policy-index__items { margin: 0; padding: 0; list-style: none; }
.policy-index--static a { display: flex; gap: .5rem; min-height: 2.75rem; align-items: center; color: var(--tq-link); }
.policy-index--static a:hover { text-decoration: underline; text-underline-offset: .25em; }
.policy-index--static a:focus-visible { outline: 2px solid var(--tq-focus); outline-offset: 2px; border-radius: var(--tq-radius-sm); }
.policy-index__items .tq-button { justify-content: flex-start; text-align: start; white-space: normal; height: auto; }
.policy-index__number { min-width: 1.5rem; flex: 0 0 auto; color: var(--tq-teal-readable); font-variant-numeric: tabular-nums; }
.policy-section { padding: 1.75rem .5rem; border-bottom: 1px solid var(--tq-border); }
.policy-section:last-child { border-bottom: 0; }
.policy-section__title { display: flex; align-items: start; gap: .75rem; margin: 0 0 1.25rem; font-size: clamp(1.1rem, 2vw, 1.35rem); line-height: 1.4; font-weight: 700; }
.policy-section__number { color: var(--tq-teal-readable); min-width: 1.75rem; font-variant-numeric: tabular-nums; }
.policy-section__subtitle { margin: 1.5rem 0 .75rem; font-size: 1rem; font-weight: 700; }
.policy-section p, .policy-section li { line-height: 1.8; font-size: 1rem; overflow-wrap: anywhere; }
.policy-section p { margin: 0 0 1rem; }
.policy-section p:last-child { margin-bottom: 0; }
.policy-section ul { display: grid; gap: .65rem; padding-left: 1.25rem; margin: 1rem 0; list-style: disc; }
.policy-section li::marker { color: var(--tq-teal-readable); }
.policy-table { overflow-x: auto; border: 1px solid var(--tq-border); border-radius: var(--tq-radius-md); margin: 1rem 0; }
.policy-table table { width: 100%; border-collapse: collapse; font-size: .925rem; line-height: 1.65; }
.policy-table th, .policy-table td { padding: .85rem 1rem; vertical-align: top; text-align: start; overflow-wrap: anywhere; }
.policy-table thead { background: var(--tq-bg-muted); }
.policy-table tbody th { width: 34%; font-weight: 600; }
.policy-table tbody tr + tr { border-top: 1px solid var(--tq-border); }
.policy-reader:focus-visible, .policy-table:focus-visible, .policy-section__title:focus-visible { outline: 2px solid var(--tq-focus); outline-offset: 2px; }
@media (max-width: 42rem) {
  .policy-quality { padding: 1rem; }
  .policy-quality dl { grid-template-columns: 1fr; gap: .5rem; }
  .policy-index { padding: .75rem; }
  .policy-index__items { grid-template-columns: 1fr; }
  .policy-section { padding-inline: .25rem; }
  .policy-table th, .policy-table td { padding: .65rem .5rem; }
  .policy-table--pairs table, .policy-table--pairs tbody, .policy-table--pairs tr,
  .policy-table--pairs tbody th, .policy-table--pairs td { display: block; width: auto; }
  .policy-table--pairs thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  .policy-table--pairs tbody th { padding: .75rem .75rem .25rem; background: var(--tq-bg-muted); }
  .policy-table--pairs td { padding: .25rem .75rem .75rem; background: var(--tq-bg-muted); }
}
@media print { .data-processing-policy-document:not(.data-processing-policy-document--standalone) { display: none !important; } }
</style>
